// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { CSSTransitionGroup } from '../../../../../node_module_clones/react-transition-group';
import classNames from 'classnames';
import { get } from 'lodash/fp';
import { Capacitor } from '@capacitor/core';

// Utils
import platformSingleton from '../../../../platform/platformSingleton';
import { isPlatformIpad } from '../../../../platform/utils/platformDetailsUtils';

// Components
import Icon from '../../../../components/icons/Icon';
import StyledButton from '../../../../components/buttons/StyledButton';
import DrawingEditorColorTool from './DrawingEditorColorTool';
import DrawingEditorStrokeSizeTool from './DrawingEditorStrokeSizeTool';
import DrawingEditorToolModeButton from './DrawingEditorToolModeButton';
import DrawingEditorToolbarIPadPenTools from './DrawingEditorToolbarIPadPenTools';
import DrawingEditorToolbarUndoRedoTools from './DrawingEditorToolbarUndoRedoTools';
import { GroupTranslatorProvider, useGroupTranslator } from '../../../../components/i18n/GroupTranslationContext';

// Hooks
import usePencilDoubleTap from './usePencilDoubleTap';
import usePrevious from '../../../../utils/react/usePrevious';
import useTouchBlockedPointerEvent from './useTouchBlockedPointerEvent';

// Constants
import { TOOL_MODES } from '../drawingEditorConstants';
import { CAPACITOR_APPLE_PENCIL_PLUGIN_NAME } from '../../../../../capacitor_plugins/pluginConstants';

// Styles
import './DrawingEditorToolbar.scss';

// If we're currently drawing, then there's no need to update the toolbar
const arePropsEqual = (prevProps, nextProps) => !!nextProps.isDrawing;

const DrawingEditorToolbar = React.memo(function DrawingEditorToolbar({
    isEditingExistingDrawing,

    toolMode,
    toolModeState,

    colorPopupOpen,
    strokePopupOpen,

    strokeSize,
    strokeColor,
    backgroundColor,
    defaultColors,

    handleSetStrokeColor,
    handleSetStrokeSize,
    handleDiscardButton,
    handleSaveButton,

    setToolMode,
    setColorPopupOpen,
    setCustomColorInputOpen,
    setStrokePopupOpen,

    canUndo,
    canRedo,
    undoPath,
    redoPath,

    isPenMode,
    setIsPenMode,
    isTouchEventInToolbarBlocked,
}) {
    const groupTranslate = useGroupTranslator();

    if (Capacitor.isPluginAvailable(CAPACITOR_APPLE_PENCIL_PLUGIN_NAME)) {
        usePencilDoubleTap({ toolMode, setToolMode, setColorPopupOpen, setStrokePopupOpen });
    }

    const prevIsEditingExistingDrawing = usePrevious(isEditingExistingDrawing);

    const isEditing = isEditingExistingDrawing || prevIsEditingExistingDrawing;

    const disablePathTools =
        toolMode === TOOL_MODES.ERASE || (toolMode === TOOL_MODES.SELECT && !get('hasSelection', toolModeState));

    const onDiscard = useTouchBlockedPointerEvent(isTouchEventInToolbarBlocked, handleDiscardButton);
    const onSave = useTouchBlockedPointerEvent(isTouchEventInToolbarBlocked, handleSaveButton);

    return (
        <div
            className={classNames('DrawingEditorToolbar', { ipad: isPlatformIpad(platformSingleton) })}
            onDoubleClick={isPenMode ? null : handleSaveButton}
        >
            <div className="tool-mode-controls">
                <DrawingEditorToolModeButton
                    name={groupTranslate('tools.pen', 'Pen')}
                    shortcut={['d']}
                    isPenMode={isPenMode}
                    currentToolMode={toolMode}
                    toolMode={TOOL_MODES.DRAW}
                    setToolMode={setToolMode}
                >
                    <Icon name="drawing-editor-tool-draw" />
                </DrawingEditorToolModeButton>
                <DrawingEditorToolModeButton
                    name={groupTranslate('tools.selectionTool', 'Selection tool')}
                    shortcut={['v']}
                    isPenMode={isPenMode}
                    currentToolMode={toolMode}
                    toolMode={TOOL_MODES.SELECT}
                    setToolMode={setToolMode}
                >
                    <Icon name="drawing-editor-tool-select" />
                </DrawingEditorToolModeButton>
                <DrawingEditorToolModeButton
                    name={groupTranslate('tools.eraser', 'Eraser')}
                    shortcut={['e']}
                    isPenMode={isPenMode}
                    currentToolMode={toolMode}
                    toolMode={TOOL_MODES.ERASE}
                    setToolMode={setToolMode}
                >
                    <Icon name="drawing-editor-tool-erase" />
                </DrawingEditorToolModeButton>
            </div>

            <div className="draw-controls">
                <DrawingEditorColorTool
                    isPenMode={isPenMode}
                    defaultColors={defaultColors}
                    disabled={disablePathTools}
                    keepPopupOpen={toolMode === TOOL_MODES.DRAW}
                    colorPopupOpen={colorPopupOpen}
                    setColorPopupOpen={setColorPopupOpen}
                    setCustomColorInputOpen={setCustomColorInputOpen}
                    backgroundColor={backgroundColor}
                    strokeColor={strokeColor}
                    setStrokeColor={handleSetStrokeColor}
                />

                <DrawingEditorStrokeSizeTool
                    isPenMode={isPenMode}
                    disabled={disablePathTools}
                    keepPopupOpen={toolMode === TOOL_MODES.SELECT}
                    strokePopupOpen={strokePopupOpen}
                    strokeSize={strokeSize}
                    setStrokeSize={handleSetStrokeSize}
                    setStrokePopupOpen={setStrokePopupOpen}
                />
            </div>

            <div className="exit-controls">
                <DrawingEditorToolbarUndoRedoTools
                    isPenMode={isPenMode}
                    canUndo={canUndo}
                    canRedo={canRedo}
                    undoPath={undoPath}
                    redoPath={redoPath}
                />
                <StyledButton className="secondary compact discard no-hover" onPointerUp={onDiscard}>
                   {isEditing ? groupTranslate('cancel', 'Cancel') : groupTranslate('discard', 'Discard')}
                </StyledButton>
                <StyledButton className="primary compact save" onPointerUp={onSave}>
                   {groupTranslate('save', 'Save')}
                </StyledButton>
            </div>
            {isPlatformIpad(platformSingleton) && (
                <DrawingEditorToolbarIPadPenTools
                    toolMode={toolMode}
                    isPenMode={isPenMode}
                    setIsPenMode={setIsPenMode}
                />
            )}
        </div>
    );
}, arePropsEqual);

DrawingEditorToolbar.propTypes = {
    toolMode: PropTypes.string,
    toolModeState: PropTypes.object,
    strokeSize: PropTypes.number,
    strokeColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    defaultColors: PropTypes.array,

    drawingModeEnabled: PropTypes.bool,
    colorPopupOpen: PropTypes.bool,
    strokePopupOpen: PropTypes.bool,
    isEditingExistingDrawing: PropTypes.bool,

    setToolMode: PropTypes.func,
    handleSetStrokeColor: PropTypes.func,
    handleSetStrokeSize: PropTypes.func,

    setColorPopupOpen: PropTypes.func,
    setCustomColorInputOpen: PropTypes.func,
    setStrokePopupOpen: PropTypes.func,

    handleDiscardButton: PropTypes.func,
    handleSaveButton: PropTypes.func,

    canUndo: PropTypes.bool,
    canRedo: PropTypes.bool,
    undoPath: PropTypes.func,
    redoPath: PropTypes.func,

    isPenMode: PropTypes.bool,
    setIsPenMode: PropTypes.func,

    isTouchEventInToolbarBlocked: PropTypes.bool,
};

const DrawingEditorToolbarWithTranslations = (props) => (
    <GroupTranslatorProvider group="popup" keyPrefix="drawing">
        <DrawingEditorToolbar {...props} />
    </GroupTranslatorProvider>
);

export default DrawingEditorToolbarWithTranslations;

export const DrawingEditorToolbarWithTransitions = (props) => (
    <CSSTransitionGroup
        transitionName="fade-and-slide"
        transitionAppear
        transitionEnterTimeout={300}
        transitionLeaveTimeout={300}
        transitionAppearTimeout={300}
    >
        <DrawingEditorToolbarWithTranslations {...props} />
    </CSSTransitionGroup>
);

DrawingEditorToolbarWithTransitions.propTypes = {
    drawingModeEnabled: PropTypes.bool,
};
